import { Firestore, doc, onSnapshot, setDoc, writeBatch } from 'firebase/firestore';
import { StorageResolver } from '@/firebase/firestore/storage-resolver';
import { normalizeDates } from '@/lib/date-utils';
import type { Arc, ArcDetails, Novel, NovelStorageLayout } from '@/lib/types';

function nestPayload(path: string, value: unknown): Record<string, unknown> {
  const parts = path.split('.');
  const result: Record<string, unknown> = {};
  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const next: Record<string, unknown> = {};
    current[parts[i]] = next;
    current = next;
  }
  current[parts[parts.length - 1]] = value;
  return result;
}

export function subscribeArcDetails(
  firestore: Firestore,
  ownerId: string,
  novelId: string,
  layout: NovelStorageLayout,
  arcId: string,
  onUpdate: (details: ArcDetails | null) => void,
  onError: (error: Error) => void
): () => void {
  const resolver = new StorageResolver(firestore);
  const { ref } = resolver.resolveItem(ownerId, novelId, 'narrative', 'arcs', arcId, layout);
  const detailsRef = doc(ref, 'details', 'main');
  return onSnapshot(
    detailsRef,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null);
        return;
      }
      onUpdate(normalizeDates({ ...(snap.data() as ArcDetails), id: 'main' }));
    },
    onError
  );
}

export async function saveArcDetails(
  firestore: Firestore,
  ownerId: string,
  novel: Novel,
  arcId: string,
  details: Partial<ArcDetails>
): Promise<void> {
  const resolver = new StorageResolver(firestore);
  const { ref } = resolver.resolveItem(ownerId, novel.id, 'narrative', 'arcs', arcId, novel.storageLayout);
  const detailsRef = doc(ref, 'details', 'main');
  await setDoc(detailsRef, details, { merge: true });
}

export async function createArc(
  firestore: Firestore,
  ownerId: string,
  novel: Novel,
  arc: Arc
): Promise<void> {
  const batch = writeBatch(firestore);
  const resolver = new StorageResolver(firestore);
  const { ref: arcRef, fieldPath } = resolver.resolveItem(
    ownerId,
    novel.id,
    'narrative',
    'arcs',
    arc.id,
    novel.storageLayout
  );

  const arcData = normalizeDates({ ...arc, updatedAt: new Date().toISOString() });

  if (fieldPath) {
    batch.update(arcRef, {
      [`entityList.${arc.id}`]: arcData,
    });
  } else {
    batch.set(arcRef, arcData);
  }

  const detailsRef = doc(arcRef, 'details', 'main');
  batch.set(detailsRef, {
    id: 'main',
    events: [],
    edges: [],
  });

  const summaryFields = [
    'id',
    'name',
    'order',
    'type',
    'preview',
    'updatedAt',
    'povCharacterId',
    'shortDescription',
  ];
  const summary: Record<string, unknown> = {};
  Object.entries(arcData).forEach(([key, value]) => {
    if (summaryFields.includes(key)) {
      summary[key] = value;
    }
  });

  const { ref: summaryRef, fieldPath: summaryFieldPath } = resolver.resolveSummary(
    ownerId,
    novel.id,
    'narrative',
    'arcs',
    arc.id,
    novel.storageLayout
  );
  if (summaryFieldPath) {
    batch.set(summaryRef, nestPayload(summaryFieldPath, summary), { merge: true });
  }

  await batch.commit();
}
